/**
 * บีบอัด/แปลงรูปเป็น WebP ใน browser (canvas)
 * ใช้ก่อนอัปโหลดเพื่อลดขนาดและรองรับทุก browser
 */

export type CompressToWebPOptions = {
  /** คุณภาพ 0–1 (default 0.85) */
  quality?: number;
  /** ความกว้างสูงสุด (คง aspect ratio) */
  maxWidth?: number;
  /** ความสูงสูงสุด (คง aspect ratio) */
  maxHeight?: number;
};

const DEFAULT_QUALITY = 0.85;
const DEFAULT_MAX_WIDTH = 2000;
const DEFAULT_MAX_HEIGHT = 2000;

/**
 * โหลด File เป็น HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

/**
 * บีบอัดและแปลงรูปเป็น WebP
 * @param file - ไฟล์รูป (image/*)
 * @returns Blob รูปแบบ image/webp
 */
export async function compressToWebP(
  file: File,
  options: CompressToWebPOptions = {},
): Promise<Blob> {
  const quality = options.quality ?? DEFAULT_QUALITY;
  const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH;
  const maxHeight = options.maxHeight ?? DEFAULT_MAX_HEIGHT;

  const img = await loadImage(file);
  const { naturalWidth, naturalHeight } = img;

  let width = naturalWidth;
  let height = naturalHeight;
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d not available");
  ctx.drawImage(img, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("toBlob failed"));
      },
      "image/webp",
      quality,
    );
  });
}
